import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, FormControlLabel, Switch, FormGroup } from '@mui/material';

import OrganizationsDropdown from '@/components/OrganizationsDropdown/OrganizationsDropdown';
import CpdTextField from '@/components/ui/CpdTextField';

const RoleBasicInfoSection = forwardRef(
  ({ role = null, onFormChange = () => {} }, ref) => {
    const [basicInfo, setBasicInfo] = useState({
      name: '',
      description: '',
      organizationId: '',
      isDefault: false,
      hasAllFacilitiesAccess: false,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (role) {
        setBasicInfo({
          name: role.name || '',
          description: role.description || '',
          organizationId: String(role.organization_id || ''),
          isDefault: role.is_default || false,
          hasAllFacilitiesAccess: role.has_all_facilities_access || false,
        });
      } else {
        setBasicInfo({
          name: '',
          description: '',
          organizationId: '',
          isDefault: false,
          hasAllFacilitiesAccess: false,
        });
      }
      setErrors({});
    }, [role]);

    useEffect(() => {
      onFormChange(basicInfo);
    }, [basicInfo, onFormChange]);

    const handleFormChange = (field, value) => {
      setBasicInfo((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: null }));
      }
    };

    const validateForm = () => {
      const newErrors = {};

      if (!basicInfo.name.trim()) {
        newErrors.name = 'Role name is required';
      }

      if (!basicInfo.organizationId) {
        newErrors.organizationId = 'Organization is required';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
      return basicInfo.name.trim() && basicInfo.organizationId;
    };

    useImperativeHandle(ref, () => ({
      validateForm,
      isFormValid: isFormValid(),
      formData: basicInfo,
    }));

    return (
      <Box>
        <CpdTextField
          label='Role Name'
          value={basicInfo.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <CpdTextField
          label='Description'
          value={basicInfo.description}
          onChange={(e) => handleFormChange('description', e.target.value)}
          multiline
          rows={2}
        />

        <OrganizationsDropdown
          value={basicInfo.organizationId}
          onChange={(e) => handleFormChange('organizationId', e.target.value)}
          label='Organization'
          required
          error={!!errors.organizationId}
          helperText={errors.organizationId}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={basicInfo.isDefault}
                onChange={(e) =>
                  handleFormChange('isDefault', e.target.checked)
                }
                color='primary'
              />
            }
            label='Set as default role for this organization'
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={basicInfo.hasAllFacilitiesAccess}
                onChange={(e) =>
                  handleFormChange('hasAllFacilitiesAccess', e.target.checked)
                }
                color='primary'
              />
            }
            label='Grant access to all facilities in this organization'
            sx={{ mt: 1 }}
          />
        </FormGroup>
      </Box>
    );
  },
);

RoleBasicInfoSection.displayName = 'RoleBasicInfoSection';

export default RoleBasicInfoSection;
